import _ from 'underscore';
import { getSiteMapItemRoute } from '@bingads-webui-react/with-site-map';
import { filterTreeItems, buildMap } from '@bingads-webui-fluent-component/selection-tree';

import {
  L1ToolConfig, SearchEngine, SiteMapItem, ToolsDialogConfig, ToolsDialogConfigInternal, TreeNode,
} from './types';

const itemMatchesSearchValue = (item, results: SiteMapItem[], searchValue: string): boolean => {
  const resultMatches = results.some((result) => result.id === item.key);
  const nameMatches = item.name && item.name.toLowerCase().includes(searchValue.toLowerCase());
  const descriptionMatches = item.description && item.description.toLowerCase().includes(searchValue.toLowerCase());
  return resultMatches || nameMatches || descriptionMatches || searchValue === '';
};

const getFilteredItems = (items: TreeNode[], results: SiteMapItem[], searchValue: string): TreeNode[] => {
  const filteredItems = _.map(items, (item) => ({
    ...item,
    flatTree: filterTreeItems(
      item.flatTree,
      item.treeMap,
      searchValue,
      (treeItem, searchQuery) => itemMatchesSearchValue(treeItem, results, searchQuery),
    ),
  }));
  const itemsWithHighlight = _.map(filteredItems, (item) => ({
    ...item,
    flatTree: item.flatTree.map((treeItem) => ({
      ...treeItem,
      highlight: searchValue,
    })),
  }));
  return _.filter(itemsWithHighlight, (item) => item.flatTree && item.flatTree.length > 0) as TreeNode[];
};

export const getFilteredConfig = (config: ToolsDialogConfigInternal, searchEngine: SearchEngine, searchValue: string): ToolsDialogConfigInternal => {
  const results = searchEngine.search(searchValue);

  const filteredGroups = config.groups.map((group) => ({
    ...group,
    items: getFilteredItems(group.items, results, searchValue),
  }));

  const nonEmptyGroups = filteredGroups.filter((group) => group.items.length > 0);

  return {
    ...config,
    groups: nonEmptyGroups,
  };
};

export const areConfigsGroupsTheSame = (config1: ToolsDialogConfigInternal, config2: ToolsDialogConfigInternal): boolean => {
  const config1GroupKeys = _.chain(config1)
    .get('groups', [])
    .map((group) => group.key)
    .value();
  const config2GroupKeys = _.chain(config2)
    .get('groups', [])
    .map((group) => group.key)
    .value();

  return _.chain(config1GroupKeys)
    .zip(config2GroupKeys)
    .all(([key1, key2]) => key1 === key2)
    .value();
};

export const getResultsCount = (config: ToolsDialogConfigInternal): number => config.groups.reduce(
  (acc, group) => acc + group.items.reduce((acc2, item) => acc2 + item.flatTree.length, 0),
  0,
);

const flattenChildren = (children: SiteMapItem[], parent: TreeNode, scope, userContext, onClick) => {
  const items = children.filter((child) => child.name && child.searchable !== false); // do not show children that don't have a name or aren't searchable
  if (!items) {
    return [];
  }

  return items.reduce((acc, item) => {
    const route = getSiteMapItemRoute({ item, scope, userContext });
    const flatTreeItem = {
      parentValue: parent.value,
      value: `${parent.value}/${item.id}`, // must make sure values are unique
      content: item.name,
      level: parent.level + 1,
      key: item.id,
      name: item.name,
      route: route.href,
      isExternal: route.isExternal,
      onClick: () => onClick({
        id: item.id,
        route: route.href,
      }),
    };
    acc.push(flatTreeItem);
    if (item.children !== undefined && item.children.length > 1 && flatTreeItem.level < 3) { // limit to depth of 3 levels
      acc.push(...flattenChildren(item.children, flatTreeItem, scope, userContext, onClick));
    }
    return acc;
  }, []);
};

const flattenTree = (root: TreeNode, flattenedChildren: TreeNode[]) => {
  if (flattenedChildren !== undefined) {
    return [root, ...flattenedChildren];
  }
  return [root];
};

const processGroupItems = (items: L1ToolConfig[], siteMap: SiteMapItem[], scope, userContext, onClick) => _.map(items, (groupItem) => {
  const siteMapItem = _.find(siteMap, (item) => groupItem.key === item.id);
  const groupItemTreeNode = {
    ...groupItem,
    value: groupItem.key,
    content: groupItem.name,
    level: 1,
  };
  // don't show children if there is only one (consistent with secondary nav)
  const flattenedChildren = siteMapItem && siteMapItem.children && siteMapItem.children.length > 1 ? flattenChildren(
    siteMapItem.children,
    groupItemTreeNode,
    scope,
    userContext,
    onClick,
  ) : [];
  const flatTree = flattenTree(groupItemTreeNode, flattenedChildren);
  return {
    ...groupItemTreeNode,
    flatTree,
    treeMap: buildMap(flatTree),
  };
});

export const updateConfigWithSiteMap = (config: ToolsDialogConfig, siteMap: SiteMapItem[], scope, userContext, onClick): ToolsDialogConfigInternal => ({
  groups: _.map(config.groups, (group) => ({
    ...group,
    items: processGroupItems(group.items, siteMap, scope, userContext, onClick),
  })),
});
